import { useCallback, useRef, type PointerEvent } from "react";
import { shallow } from "zustand/shallow";
import Logo from "@core/layouts/header/components/common/Logo";
import CnbPc from "@core/layouts/header/components/pc/CnbPc";
import UserPc from "@core/layouts/header/components/pc/UserPc";
import UtilPc from "@core/layouts/header/components/pc/UtilPc";
import HelpDesk from "@core/layouts/header/components/pc/HelpDesk";
import { useUiStore } from "@core/stores/uiStore";
import { cn } from "@core/utils/domUtil";

export default function HeaderPc({ isLoginLoading }: { isLoginLoading: boolean }) {
  const { isCnbOpen, setIsCnbOpen } = useUiStore(
    (state) => ({
      isCnbOpen: state.isCnbOpen,
      setIsCnbOpen: state.setIsCnbOpen,
    }),
    shallow,
  );

  const headerRef = useRef<HTMLDivElement | null>(null);

  const pointerOverHandler = useCallback(() => {
    if (!isCnbOpen) setIsCnbOpen(true);
  }, [isCnbOpen, setIsCnbOpen]);

  const pointerOutHandler = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const relatedTarget = e.relatedTarget as HTMLDivElement;
      if (relatedTarget && headerRef.current?.contains(relatedTarget)) {
        return;
      }
      if (isCnbOpen) setIsCnbOpen(false);
    },
    [isCnbOpen, setIsCnbOpen],
  );

  return (
    <div className={cn("sticky top-0 right-0 left-0 z-50 h-[var(--pc-header-height)] bg-white")} onPointerOut={pointerOutHandler} ref={headerRef}>
      <div
        className={cn(
          "transition-height absolute top-0 left-0 h-[var(--pc-header-height)] w-full overflow-hidden bg-white shadow-[0px_2px_30px_rgba(0,0,0,0.1)] duration-200 ease-out",
          isCnbOpen && "h-[var(--pc-header-full-height)]",
        )}
      >
        <div className="flex h-[var(--pc-header-height)] items-center justify-between border-b-1 border-gray-300 px-[30px]">
          <Logo />
          <CnbPc isOpen={isCnbOpen} pointerOverHandler={pointerOverHandler} />
          <div className="flex items-center gap-2 justify-end w-[295px]">
            {!isLoginLoading ? (
              <>
                <UserPc />
                <UtilPc />
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
        <HelpDesk />
      </div>
    </div>
  );
}
